'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { DEFAULT_DEBOUNCE_TIME, SEARCH_MAX_LIMIT } from '@/constants/static';
import { CategoryType } from '@/enums/CategoryType';
import ProductListingDictionary from '@/types/dictionaries/ProductListing';
import StockAvailabilityDictionary from '@/types/dictionaries/StockAvailability';
import { Category } from '@/types/gearApi/Category';
import { ProductFilter, ProductSearchResult, SearchResult } from '@/types/gearApi/Product';
import { ApiResult } from '@/types/gearApi/Result';
import { WishlistResponse } from '@/types/gearApi/Wishlist';
import { getBusinessUnitKey } from '@/utils/helpers/businessUnit';
import { updateURLParams } from '@/utils/helpers/url';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
	buildClampedRangeParams,
	extractEnumFilters,
	extractFilterLimits,
	parseEnumParams,
	parseNumericRangeParams,
} from '@/utils/helpers/filters';
import { FilterParamKey } from '@/enums/FilterParam';
import { NormalizedLocale } from '@/enums/Locale';
import FilterLabelsDictionary from '@/types/dictionaries/FilterLabels';
import PaginationRenderer from '../../components/PaginationRenderer';
import { EnumsFilterInput, getRefinedProductsWithConfig } from './actions';
import ProductCount from './components/ProductCount';
import ProductList from './components/ProductList';
import ProductListSkeleton from './components/ProductListSkeleton';
import ProductsRefinement from './components/ProductsRefinement';
import FullWidthContainer from '@/components/FullWidthContainer';

interface FeatureRefinedProductListProps {
	languageCode: NormalizedLocale;
	brandCode: string;
	categoryKey?: string;
	productCategories: Category[];
	industryCategories: Category[];
	wishlists?: WishlistResponse;
	dict: ProductListingDictionary;
	isBusinessToBusinessShop: boolean;
	stockAvailabilityDict: StockAvailabilityDictionary;
	sharedCartId: string | null;
	filterLabelsDict: FilterLabelsDictionary;
	requestQuoteUrl: string | null;
	contactUsUrl: string;
	withBackground?: boolean;
}

export default function FeatureRefinedProductList({
	languageCode,
	brandCode,
	categoryKey,
	productCategories,
	industryCategories,
	wishlists,
	dict,
	isBusinessToBusinessShop,
	stockAvailabilityDict,
	sharedCartId,
	filterLabelsDict,
	requestQuoteUrl,
	contactUsUrl,
	withBackground,
}: Readonly<FeatureRefinedProductListProps>) {
	/** TODO: Temporary solution to manually set loading state when calling get search products api
	 * as <Suspense fallback> does not work as expected.
	 * Tried the official implementation of Suspense in this tutorial (https://nextjs.org/learn/dashboard-app/streaming), it is also not working:
	 * Related Suspense bug in nextJS repo: https://github.com/vercel/next.js/issues?q=is%3Aissue+is%3Aopen+Suspense
	 * Once Suspense works again, partial streaming should work to load each component individually
	 */
	const debounceRef = useRef(false);
	const [isLoading, setIsLoading] = useState(true);
	const [productSearchResult, setProductSearchResult] = useState<ApiResult<SearchResult<ProductSearchResult>> | null>(
		null,
	);
	const [filterConfigResult, setFilterConfigResult] = useState<ApiResult<ProductFilter[]> | null>(null);

	const filterLimits = extractFilterLimits(filterConfigResult?.data ?? []);
	const enumFilterKeys = extractEnumFilters(filterConfigResult?.data ?? []);

	const searchParams = useSearchParams();
	const initialSelectedCategories = searchParams.get(CategoryType.CATEGORIES)?.split(',') ?? null;
	const initialSelectedIndustries = searchParams.get(CategoryType.INDUSTRIES)?.split(',') ?? null;
	const initialSelectedProductReference = searchParams.get(FilterParamKey.FitFor) ?? null;

	const searchQuery = searchParams.get(FilterParamKey.Search);

	const [paginationStep, setPaginationStep] = useState(parseInt(searchParams.get(FilterParamKey.Page) ?? '1', 10));
	const [paginationStepCount, setPaginationStepCount] = useState(1);

	const [prevParams, setPrevParams] = useState<string | null>(null);

	const { replace } = useRouter();
	const pathname = usePathname();

	const listingTopRef = useRef<HTMLDivElement | null>(null);
	const { data: session } = useSession();
	const [prevBusinessUnitKey, setPrevBusinessUnitKey] = useState<string | null>(null);

	const handleFilterChange = async (
		selectedCategories: string[] | null,
		selectedIndustries: string[] | null,
		searchTerm: string | null,
		page: number,
		category?: string,
		selectedProductReference?: string | null,
		numericFilterTuples?: Record<string, [number, number]>,
		selectedEnums?: EnumsFilterInput,
	) => {
		// Calculate the offset for pagination. This helps retrieve the correct subset of products for the current page.
		const offset = (page - 1) * SEARCH_MAX_LIMIT;

		const { productResult, filterConfigResult } = await getRefinedProductsWithConfig({
			selectedCategories,
			selectedIndustries,
			categoryKey: category,
			searchQuery: searchTerm,
			offset,
			limit: SEARCH_MAX_LIMIT,
			selectedProductReference,
			numericRanges: numericFilterTuples,
			selectedEnums,
		});

		setProductSearchResult(productResult);
		setFilterConfigResult(filterConfigResult);
		setIsLoading(false);
	};

	const handlePaginationStepChange = (step: number) => {
		setPaginationStep(step);

		const newParams = { page: step.toString() };
		const updatedParams = updateURLParams(searchParams, newParams);
		replace(`${pathname}?${updatedParams.toString()}`);

		listingTopRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const handleFilterUpdate = useCallback(
		async (
			selectedCategories: string[] | null,
			selectedIndustries: string[] | null,
			numericRanges: Record<string, number | undefined>,
			selectedProductReference?: string | null,
			selectedEnums?: Record<string, string[] | null>,
		) => {
			debounceRef.current = true;

			const newPage = 1;
			setPaginationStep(newPage);
			setIsLoading(true);

			const newParams: Record<string, string | null> = {
				[CategoryType.CATEGORIES]: selectedCategories?.length ? selectedCategories.join(',') : null,
				[CategoryType.INDUSTRIES]: selectedIndustries?.length ? selectedIndustries.join(',') : null,
				[FilterParamKey.FitFor]: selectedProductReference ?? null,
				page: newPage.toString(),
			};

			// Add enum filter params
			for (const [enumKey, enumValues] of Object.entries(selectedEnums ?? {})) {
				newParams[enumKey] = enumValues?.length ? enumValues.join(',') : null;
			}

			// Add numeric range params
			for (const [key, value] of Object.entries(numericRanges)) {
				newParams[key] = typeof value === 'number' ? value.toString() : null;
			}

			const updatedParams = updateURLParams(searchParams, newParams);
			replace(`${pathname}?${updatedParams.toString()}`);

			const numericTuples = parseNumericRangeParams(numericRanges, filterLimits);

			await handleFilterChange(
				selectedCategories,
				selectedIndustries,
				searchQuery,
				newPage,
				categoryKey,
				selectedProductReference ?? null,
				numericTuples,
				selectedEnums,
			);

			setTimeout(() => {
				debounceRef.current = false;
			}, DEFAULT_DEBOUNCE_TIME);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[searchParams, pathname, searchQuery],
	);

	// Ensures numeric range values in the URL stay within the configured filter limits.
	useEffect(() => {
		if (!filterConfigResult?.data) return;

		const clampedTuples = parseNumericRangeParams(searchParams, filterLimits);
		const clampedParams = buildClampedRangeParams(clampedTuples, filterLimits);

		const current = Object.fromEntries(searchParams.entries());
		const updatedParams = {
			...current,
			...Object.fromEntries(Object.entries(clampedParams).map(([key, value]) => [key, value.toString()])),
		};

		// Detect if any clamping actually changed searchParams
		const hasChanged = Object.entries(clampedParams).some(([key, clamped]) => {
			const original = searchParams.get(key);
			return original !== clamped.toString();
		});

		// If changed, replace URL with clamped values
		if (hasChanged) {
			const search = new URLSearchParams(updatedParams);
			replace(`${pathname}?${search.toString()}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, filterConfigResult, pathname]);

	useEffect(() => {
		if (debounceRef.current) return;

		const currentParams = searchParams.toString();
		if (currentParams !== prevParams) {
			void handleFilterChange(
				initialSelectedCategories,
				initialSelectedIndustries,
				searchQuery,
				paginationStep,
				categoryKey,
				initialSelectedProductReference,
				parseNumericRangeParams(searchParams, filterLimits),
				parseEnumParams(searchParams, enumFilterKeys),
			);
			setPrevParams(currentParams);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, paginationStep, categoryKey]);

	useEffect(() => {
		const currentBusinessUnitKey = (session && getBusinessUnitKey(session)) ?? null;
		if (currentBusinessUnitKey !== prevBusinessUnitKey) {
			void handleFilterChange(
				initialSelectedCategories,
				initialSelectedIndustries,
				searchQuery,
				paginationStep,
				categoryKey,
				initialSelectedProductReference,
				parseNumericRangeParams(searchParams, filterLimits),
				parseEnumParams(searchParams, enumFilterKeys),
			);
			setPrevBusinessUnitKey(currentBusinessUnitKey);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session]);

	// Effect to calculate pagination step count
	useEffect(() => {
		if (productSearchResult?.data?.total) {
			setPaginationStepCount(Math.ceil(productSearchResult.data.total / SEARCH_MAX_LIMIT));
		}
	}, [productSearchResult]);

	// Handle error in product search result
	useEffect(() => {
		if ((productSearchResult && !productSearchResult.ok) || (filterConfigResult && !filterConfigResult.ok)) {
			throw new Error(dict.messages.error);
		}
	}, [productSearchResult, filterConfigResult, dict.messages.error]);

	if (isLoading && !productSearchResult) {
		return <ProductListSkeleton />;
	}

	return (
		<Box ref={listingTopRef}>
			<FullWidthContainer withBackground={withBackground}>
				<ProductsRefinement
					brandCode={brandCode}
					categories={productCategories}
					industries={industryCategories}
					filterLabels={dict.filters}
					categoryKey={categoryKey}
					filterConfig={filterConfigResult?.data ?? []}
					onSelect={handleFilterUpdate}
					filterLabelsDict={filterLabelsDict}
					withLabelBackground={withBackground}
				/>
			</FullWidthContainer>

			{isLoading ? (
				<ProductListSkeleton />
			) : (
				<>
					{!productSearchResult?.data || productSearchResult.data.results.length === 0 ? (
						<Box sx={{ overflowY: 'hidden', height: '100vh', mt: '16px' }}>
							<Typography variant="paragraph">{dict.messages.noProductsFound}</Typography>
						</Box>
					) : (
						<>
							<ProductCount count={productSearchResult.data.total} dict={dict} />
							<ProductList
								products={productSearchResult.data.results}
								languageCode={languageCode}
								wishlists={wishlists}
								dict={dict}
								stockAvailabilityDict={stockAvailabilityDict}
								sharedCartId={sharedCartId}
								isBusinessToBusinessShop={isBusinessToBusinessShop}
								requestQuoteUrl={requestQuoteUrl}
								contactUsUrl={contactUsUrl}
							/>
							<PaginationRenderer
								currentStep={paginationStep}
								stepCount={paginationStepCount}
								onStepChange={handlePaginationStepChange}
							/>
						</>
					)}
				</>
			)}
		</Box>
	);
}
